import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { authService } from '../../../services/authService';
import { groupBookingsService } from '../../../services/groupBookingsService';
import travelieTheme from '../../../styles/travelieTheme';
import { useResponsive } from '../../../hooks/useResponsive';

// Phase 6: Migrated to TravelieTheme - June 16, 2026

const SecureGroupPaymentModal = ({ participant, onClose }) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();

  // Helper function for button styling
  const getButtonStyle = (type, disabled = false) => {
    const baseStyle = {
      padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
      fontSize: travelieTheme.typography.fontSize.sm,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: `all ${travelieTheme.transitions.fast}`,
      opacity: disabled ? 0.5 : 1
    };

    const styles = {
      primary: { ...baseStyle, background: travelieTheme.colors.primary.main, color: '#FFFFFF' },
      secondary: { ...baseStyle, background: travelieTheme.colors.neutral.border, color: travelieTheme.colors.text.primary },
      danger: { ...baseStyle, background: travelieTheme.colors.status.error, color: '#FFFFFF' }
    };

    return styles[type] || styles.primary;
  };

  // Helper function for payment status badge
  const getPaymentStatusStyle = (status) => {
    const baseStyle = {
      padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`,
      fontSize: travelieTheme.typography.fontSize.xs,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      display: 'inline-block',
      textTransform: 'capitalize'
    };

    const statusStyles = {
      completed: { ...baseStyle, background: '#DCFCE7', color: '#166534' },
      pending: { ...baseStyle, background: '#FEF3C7', color: '#92400E' },
      partial: { ...baseStyle, background: '#DBEAFE', color: '#1E40AF' },
      failed: { ...baseStyle, background: '#FEE2E2', color: '#991B1B' }
    };

    return statusStyles[status] || statusStyles.pending;
  };
  const [step, setStep] = useState('verify'); // 'verify', 'code', 'details'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes
  const [sessionExpired, setSessionExpired] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState('email');

  useEffect(() => {
    let timer;
    if (step === 'details' && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setSessionExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [step, timeLeft]);

  const handleRequestVerification = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Request 2FA code for group payment access
      const response = await authService.requestGroupPaymentAccess(participant.id, verificationMethod);
      console.log('Verification request response:', response);
      setStep('code');
    } catch (error) {
      console.error('Error requesting verification:', error);
      console.error('Error response:', error.response);
      
      let errorMessage = 'Failed to send verification code';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Verify code and get payment details
      const details = await authService.getGroupPaymentDetails(participant.id, verificationCode);
      setPaymentDetails(details.data);
      setStep('details');
      setTimeLeft(600); // 10 minutes for viewing
    } catch (error) {
      console.error('Error verifying code:', error);
      console.error('Error response:', error.response);
      
      let errorMessage = 'Invalid verification code';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayment = async (participantId) => {
    if (!window.confirm('Are you sure you want to delete this payment information? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await groupBookingsService.deleteParticipantPayment(participantId);
      
      // Close modal and show success message
      alert('Payment information has been successfully deleted.');
      onClose();
    } catch (error) {
      console.error('Error deleting payment:', error);
      setError(error.response?.data?.error || 'Failed to delete payment information');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getBillingAddress = (billingData) => {
    if (!billingData) return 'N/A';
    try {
      const parsed = typeof billingData === 'string' ? JSON.parse(billingData) : billingData;
      return `${parsed.address || ''}, ${parsed.city || ''}, ${parsed.state || ''} ${parsed.zip || ''}`.trim();
    } catch {
      return 'N/A';
    }
  };

  const formatCardNumber = (cardNumber) => {
    if (!cardNumber) return 'N/A';
    // Format as XXXX XXXX XXXX XXXX
    return cardNumber.replace(/(.{4})/g, '$1 ').trim();
  };

  const maskCardNumber = (cardNumber) => {
    if (!cardNumber) return 'N/A';
    // Show only last 4 digits
    return `**** **** **** ${cardNumber.slice(-4)}`;
  };

  if (sessionExpired) {
    return (
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0, 0, 0, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: travelieTheme.spacing.md }}>
        <div style={{ background: "#FFFFFF", borderRadius: travelieTheme.components.card.borderRadius, maxWidth: "800px", width: "100%", maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}>
          <div style={{ padding: travelieTheme.spacing.lg, borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h2>Session Expired</h2>
            <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "28px", cursor: "pointer", color: travelieTheme.colors.text.secondary, padding: 0, width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", transition: `color ${travelieTheme.transitions.fast}` }}>×</button>
          </div>
          <div style={{ padding: travelieTheme.spacing.lg }}>
            <div style={{ textAlign: "center", padding: travelieTheme.spacing.xl }}>
              <div style={{ fontSize: "64px", marginBottom: travelieTheme.spacing.md }}>⏰</div>
              <h3>Security Session Expired</h3>
              <p>For security reasons, your access to payment details has expired.</p>
              <p>Please close this window and request access again if needed.</p>
            </div>
          </div>
          <div style={{ padding: travelieTheme.spacing.lg, borderTop: `1px solid ${travelieTheme.colors.neutral.border}`, display: "flex", justifyContent: "flex-end", gap: travelieTheme.spacing.sm }}>
            <button onClick={onClose} style={getButtonStyle("primary", loading)}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0, 0, 0, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: travelieTheme.spacing.md }}>
      <div style={{ background: "#FFFFFF", borderRadius: travelieTheme.components.card.borderRadius, maxWidth: "800px", width: "100%", maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}>
        <div style={{ padding: travelieTheme.spacing.lg, borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2>Secure Payment Details - {participant.first_name} {participant.last_name}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "28px", cursor: "pointer", color: travelieTheme.colors.text.secondary, padding: 0, width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", transition: `color ${travelieTheme.transitions.fast}` }}>×</button>
        </div>

        <div style={{ padding: travelieTheme.spacing.lg }}>
          {step === 'verify' && (
            <div style={{ textAlign: "center", padding: travelieTheme.spacing.lg }}>
              <div style={{ fontSize: "48px", marginBottom: travelieTheme.spacing.md }}>🔐</div>
              <h3>Security Verification Required</h3>
              
              <div style={{ marginTop: travelieTheme.spacing.lg, marginBottom: travelieTheme.spacing.lg }}>
                <h4>Choose verification method:</h4>
                <div style={{ display: "flex", gap: travelieTheme.spacing.md, justifyContent: "center", marginTop: travelieTheme.spacing.md }}>
                  <label style={{ display: "flex", alignItems: "center", gap: travelieTheme.spacing.xs, padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`, border: `2px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="verificationMethod"
                      value="email"
                      checked={verificationMethod === 'email'}
                      onChange={(e) => setVerificationMethod(e.target.value)}
                    />
                    <span>📧 Email</span>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: travelieTheme.spacing.xs, padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`, border: `2px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="verificationMethod"
                      value="sms"
                      checked={verificationMethod === 'sms'}
                      onChange={(e) => setVerificationMethod(e.target.value)}
                    />
                    <span>📱 SMS</span>
                  </label>
                </div>
              </div>

              <div style={{ marginTop: travelieTheme.spacing.lg, padding: travelieTheme.spacing.md, background: "#F9FAFB", borderRadius: travelieTheme.components.card.borderRadius, textAlign: "left" }}>
                <h4>Participant Information:</h4>
                <p><strong>Name:</strong> {participant.first_name} {participant.last_name}</p>
                <p><strong>Email:</strong> {participant.email}</p>
                <p><strong>Package:</strong> {participant.package_selected}</p>
                <p><strong>Amount:</strong> ${parseFloat(participant.total_amount || 0).toFixed(2)}</p>
              </div>

              {error && <div style={{ padding: travelieTheme.spacing.sm, background: "#FEE2E2", color: "#991B1B", borderRadius: travelieTheme.components.card.borderRadius, marginTop: travelieTheme.spacing.md, fontSize: travelieTheme.typography.fontSize.sm }}>{error}</div>}

              <div style={{ display: "flex", gap: travelieTheme.spacing.sm, justifyContent: "center", marginTop: travelieTheme.spacing.lg }}>
                <button onClick={onClose} style={getButtonStyle("secondary")}>Cancel</button>
                <button 
                  onClick={handleRequestVerification} 
                  disabled={loading}
                  style={getButtonStyle("primary", loading)}
                >
                  {loading ? 'Sending...' : 'Send Verification Code'}
                </button>
              </div>
            </div>
          )}

          {step === 'code' && (
            <div style={{ textAlign: "center", padding: travelieTheme.spacing.lg }}>
              <div style={{ fontSize: "48px", marginBottom: travelieTheme.spacing.md }}>📧</div>
              <h3>Enter Verification Code</h3>
              <p>We've sent a 6-digit verification code to your registered email address.</p>
              
              <div style={{ marginTop: travelieTheme.spacing.lg }}>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  style={{ width: "100%", maxWidth: "300px", padding: travelieTheme.spacing.md, fontSize: "24px", fontFamily: "monospace", textAlign: "center", border: `2px solid ${travelieTheme.colors.primary.main}`, borderRadius: travelieTheme.components.card.borderRadius, letterSpacing: "8px", outline: "none" }}
                  maxLength="6"
                />
              </div>

              {error && <div style={{ padding: travelieTheme.spacing.sm, background: "#FEE2E2", color: "#991B1B", borderRadius: travelieTheme.components.card.borderRadius, marginTop: travelieTheme.spacing.md, fontSize: travelieTheme.typography.fontSize.sm }}>{error}</div>}

              <div style={{ display: "flex", gap: travelieTheme.spacing.sm, justifyContent: "center", marginTop: travelieTheme.spacing.lg }}>
                <button onClick={() => setStep('verify')} style={getButtonStyle("secondary")}>Back</button>
                <button 
                  onClick={handleVerifyCode} 
                  disabled={loading || verificationCode.length !== 6}
                  style={getButtonStyle("primary", loading)}
                >
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>
              </div>
            </div>
          )}

          {step === 'details' && paymentDetails && (
            <div style={{ padding: travelieTheme.spacing.lg }}>
              <div style={{ padding: travelieTheme.spacing.sm, background: "#FEF3C7", color: "#92400E", borderRadius: travelieTheme.components.card.borderRadius, marginBottom: travelieTheme.spacing.lg, textAlign: "center", fontSize: travelieTheme.typography.fontSize.sm }}>
                <span style={{ marginRight: travelieTheme.spacing.xs }}>⏱️</span>
                Session expires in: <strong>{formatTime(timeLeft)}</strong>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.lg }}>
                <div style={{ padding: travelieTheme.spacing.lg, background: "#FFFFFF", border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, boxShadow: travelieTheme.components.card.boxShadow }}>
                  <h3>Participant Information</h3>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md, marginTop: travelieTheme.spacing.md }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                      <label>Full Name:</label>
                      <span>{participant.first_name} {participant.last_name}</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                      <label>Email:</label>
                      <span>{participant.email}</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                      <label>Phone:</label>
                      <span>{participant.phone}</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                      <label>Package:</label>
                      <span>{participant.package_selected}</span>
                    </div>
                  </div>
                </div>

                <div style={{ padding: travelieTheme.spacing.lg, background: "#FFFFFF", border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, boxShadow: travelieTheme.components.card.boxShadow }}>
                  <h3>Payment Information</h3>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md, marginTop: travelieTheme.spacing.md }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                      <label>Total Amount:</label>
                      <span>${parseFloat(participant.total_amount || 0).toFixed(2)}</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                      <label>Payment Status:</label>
                      <span style={getPaymentStatusStyle(participant.payment_status || 'pending')}>
                        {participant.payment_status || 'pending'}
                      </span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                      <label>Registration Date:</label>
                      <span>{formatDate(participant.joined_at)}</span>
                    </div>
                  </div>
                </div>

                {paymentDetails.card_details && (
                  <div style={{ padding: travelieTheme.spacing.lg, background: "#FEF3C7", border: "1px solid #FDE047", borderRadius: travelieTheme.components.card.borderRadius, boxShadow: travelieTheme.components.card.boxShadow }}>
                    <h3>🔒 Credit Card Details</h3>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md, marginTop: travelieTheme.spacing.md }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                        <label>Card Number:</label>
                        <span style={{ fontSize: travelieTheme.typography.fontSize.lg, fontFamily: "monospace", letterSpacing: "2px", color: "#92400E", fontWeight: travelieTheme.typography.fontWeight.semibold, display: "block", marginTop: travelieTheme.spacing.sm }}>{formatCardNumber(paymentDetails.card_details.number)}</span>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                        <label>Expiry Date:</label>
                        <span style={{ padding: travelieTheme.spacing.md, background: "#FEF3C7", border: "1px solid #FDE047", borderRadius: travelieTheme.components.card.borderRadius, marginTop: travelieTheme.spacing.md }}>{paymentDetails.card_details.exp_month}/{paymentDetails.card_details.exp_year}</span>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                        <label>CVV:</label>
                        <span style={{ padding: travelieTheme.spacing.md, background: "#FEF3C7", border: "1px solid #FDE047", borderRadius: travelieTheme.components.card.borderRadius, marginTop: travelieTheme.spacing.md }}>{paymentDetails.card_details.cvv}</span>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                        <label>Cardholder Name:</label>
                        <span style={{ padding: travelieTheme.spacing.md, background: "#FEF3C7", border: "1px solid #FDE047", borderRadius: travelieTheme.components.card.borderRadius, marginTop: travelieTheme.spacing.md }}>{paymentDetails.card_details.name}</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: travelieTheme.spacing.sm, marginTop: travelieTheme.spacing.md }}>
                      <button 
                        onClick={() => handleDeletePayment(participant.id)}
                        style={getButtonStyle('danger')}
                        title="Delete payment information"
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                      >
                        🗑️ Delete Payment Data
                      </button>
                    </div>
                  </div>
                )}

                {paymentDetails.billing_address && (
                  <div style={{ padding: travelieTheme.spacing.lg, background: "#FFFFFF", border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, boxShadow: travelieTheme.components.card.boxShadow }}>
                    <h3>Billing Address</h3>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md, marginTop: travelieTheme.spacing.md }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs, gridColumn: isMobile ? "1" : "1 / -1" }}>
                        <label>Address:</label>
                        <span>{getBillingAddress(paymentDetails.billing_address)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {paymentDetails.payment_sessions && paymentDetails.payment_sessions.length > 0 && (
                  <div style={{ padding: travelieTheme.spacing.lg, background: "#FFFFFF", border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, boxShadow: travelieTheme.components.card.boxShadow }}>
                    <h3>💳 Payment Sessions</h3>
                    {paymentDetails.payment_sessions.map((session, index) => (
                      <div key={index} style={{ padding: travelieTheme.spacing.md, background: "#F9FAFB", border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, marginBottom: travelieTheme.spacing.sm }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: travelieTheme.spacing.sm }}>
                          <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary }}>Session ID: {session.session_id}</span>
                          <span style={{ fontSize: travelieTheme.typography.fontSize.md, fontWeight: travelieTheme.typography.fontWeight.semibold, color: travelieTheme.colors.status.success }}>${parseFloat(session.amount || 0).toFixed(2)}</span>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md, marginTop: travelieTheme.spacing.md }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                            <label>Status:</label>
                            <span style={getPaymentStatusStyle(session.status || 'pending')}>
                              {session.status || 'pending'}
                            </span>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                            <label>Created:</label>
                            <span>{formatDate(session.created_at)}</span>
                          </div>
                          {session.payment_method && (
                            <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                              <label>Payment Method:</label>
                              <span>{session.payment_method}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {paymentDetails.payments && paymentDetails.payments.length > 0 && (
                  <div style={{ padding: travelieTheme.spacing.lg, background: "#FFFFFF", border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, boxShadow: travelieTheme.components.card.boxShadow }}>
                    <h3>✅ Completed Payments</h3>
                    {paymentDetails.payments.map((payment, index) => (
                      <div key={index} style={{ padding: travelieTheme.spacing.md, background: "#F9FAFB", border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, marginBottom: travelieTheme.spacing.sm }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: travelieTheme.spacing.sm }}>
                          <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary }}>Transaction ID: {payment.transaction_id}</span>
                          <span style={{ fontSize: travelieTheme.typography.fontSize.md, fontWeight: travelieTheme.typography.fontWeight.semibold, color: travelieTheme.colors.status.success }}>${parseFloat(payment.amount || 0).toFixed(2)}</span>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md, marginTop: travelieTheme.spacing.md }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                            <label>Status:</label>
                            <span style={getPaymentStatusStyle(payment.status || 'completed')}>
                              {payment.status || 'completed'}
                            </span>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                            <label>Payment Date:</label>
                            <span>{formatDate(payment.created_at)}</span>
                          </div>
                          {payment.payment_method && (
                            <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                              <label>Payment Method:</label>
                              <span>{payment.payment_method}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {participant.booking_details && (
                  <div style={{ padding: travelieTheme.spacing.lg, background: "#FFFFFF", border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, boxShadow: travelieTheme.components.card.boxShadow }}>
                    <h3>📋 Booking Details</h3>
                    {(() => {
                      const bookingDetails = typeof participant.booking_details === 'string' ? 
                        JSON.parse(participant.booking_details) : participant.booking_details;
                      return (
                        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md, marginTop: travelieTheme.spacing.md }}>
                          {bookingDetails.address && (
                            <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs, gridColumn: isMobile ? "1" : "1 / -1" }}>
                              <label>Participant Address:</label>
                              <span>{bookingDetails.address}</span>
                            </div>
                          )}
                          {bookingDetails.special_requests && (
                            <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs, gridColumn: isMobile ? "1" : "1 / -1" }}>
                              <label>Special Requests:</label>
                              <span>{bookingDetails.special_requests}</span>
                            </div>
                          )}
                          {bookingDetails.additional_guests && bookingDetails.additional_guests.length > 0 && (
                            <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs, gridColumn: isMobile ? "1" : "1 / -1" }}>
                              <label>Additional Guests ({bookingDetails.additional_guests.length}):</label>
                              <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs, marginTop: travelieTheme.spacing.sm }}>
                                {bookingDetails.additional_guests.map((guest, idx) => (
                                  <div key={idx} style={{ padding: travelieTheme.spacing.sm, background: "#F3F4F6", borderRadius: travelieTheme.components.card.borderRadius, fontSize: travelieTheme.typography.fontSize.sm }}>
                                    <strong>{guest.name}</strong> {guest.age && `(Age: ${guest.age})`}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              <div style={{ display: "flex", gap: travelieTheme.spacing.sm, justifyContent: "flex-end", marginTop: travelieTheme.spacing.lg, paddingTop: travelieTheme.spacing.lg, borderTop: `1px solid ${travelieTheme.colors.neutral.border}` }}>
                <button onClick={onClose} style={getButtonStyle("primary", loading)}>Close</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecureGroupPaymentModal;
